/**
 * The side-by-side run (Diurnal parity C3, 02-060; Diurnal inventory 02 §9 "Parallel running"; migration plan
 * "Side-by-side run clean"; decision 111: no retirement date, MB phases the Diurnal tool out in his own time): one day
 * of one base station, Teslyn Cloud's exceedance runs against the exceedance workbook the Diurnal tool (Ctrl+J) or
 * Teslyn Field ("Excel exceedance report") wrote for the same TA Live day. The workbook's rows are pieces of runs cut
 * at survey lines (`exceedanceWorkbookRows`), so a row agrees with a cloud run it lies inside; the cloud has no survey
 * lines. A run of one reading is left out of the comparison, as the workbook leaves it out.
 */
import { WORKBOOK_COLUMNS } from './diurnal-exports.js';
import type { CloudQcItem } from './diurnal-cloud.js';
import { localParts, localToUtcMs } from './time-model.js';

/** Seconds the start or end of a row and a run may differ and still agree (the readings are a second apart). */
export const SIDE_BY_SIDE_TOLERANCE_S = 2;

export type SideBySideSource = 'diurnal_tool' | 'teslyn_field';

export const SIDE_BY_SIDE_SOURCE_LABEL: Readonly<Record<SideBySideSource, string>> = {
  diurnal_tool: 'Diurnal tool',
  teslyn_field: 'Teslyn Field',
};

/** One row of the workbook: an exceedance on a line, or "Aircraft Offline". */
export interface WorkbookPeriod {
  readonly jday: number | null;
  readonly start_sod: number;
  readonly end_sod: number;
  readonly line: string;
  readonly flight: string;
  readonly source: string;
}

export type WorkbookRead =
  | { readonly ok: true; readonly periods: readonly WorkbookPeriod[]; readonly jdays: readonly number[] }
  | { readonly ok: false; readonly message: string };

const norm = (s: string): string => s.trim().toLowerCase().replace(/\s+/g, ' ');

/** The exceedance rows of a workbook's cells (the TOTAL and Summary rows left out). */
export function readExceedanceWorkbook(rows: readonly (readonly string[])[]): WorkbookRead {
  const head = rows[0]?.map(norm) ?? [];
  const col = (name: (typeof WORKBOOK_COLUMNS)[number]): number => head.indexOf(norm(name));
  const need = {
    jday: col('Julian Day'),
    start: col('Start Time (Seconds past Midnight)'),
    end: col('End Time (Seconds past Midnight)'),
    line: col('Line Number'),
    flight: col('Flt number'),
    source: col('Exceedance Source'),
  };
  if (need.start < 0 || need.end < 0 || need.line < 0)
    return {
      ok: false,
      message:
        'This is not an exceedance workbook: its first row should hold "Start Time (Seconds past Midnight)", "End Time (Seconds past Midnight)" and "Line Number" (the Diurnal tool’s Excel Exceedance Report, Ctrl+J, or Teslyn Field’s).',
    };
  const periods: WorkbookPeriod[] = [];
  const jdays = new Set<number>();
  for (const r of rows.slice(1)) {
    const line = (r[need.line] ?? '').trim();
    const source = need.source >= 0 ? (r[need.source] ?? '').trim() : '';
    const s = Number(r[need.start]);
    const e = Number(r[need.end]);
    if (source === 'Summary' || / TOTAL$/.test(line) || !(r[need.start] ?? '').trim()) continue;
    if (!Number.isFinite(s) || !Number.isFinite(e)) continue;
    const j = need.jday >= 0 ? Number(r[need.jday]) : NaN;
    if (Number.isFinite(j)) jdays.add(j);
    periods.push({
      jday: Number.isFinite(j) ? j : null,
      start_sod: s,
      end_sod: e,
      line,
      flight: need.flight >= 0 ? (r[need.flight] ?? '').trim() : '',
      source,
    });
  }
  return { ok: true, periods, jdays: [...jdays].sort((a, b) => a - b) };
}

export type RunVerdict = 'agrees' | 'times_differ' | 'cloud_only';

export interface SideBySideRun {
  readonly start_ms: number;
  readonly end_ms: number;
  readonly readings: number;
  readonly metrics: readonly string[];
  readonly verdict: RunVerdict;
  /** The workbook rows inside this run. */
  readonly rows: readonly WorkbookPeriod[];
}

export interface SideBySideResult {
  readonly day: string;
  readonly jday: number;
  readonly tolerance_s: number;
  readonly runs: readonly SideBySideRun[];
  /** Workbook rows inside no cloud run. */
  readonly workbook_only: readonly WorkbookPeriod[];
  /** Runs of one reading, left out as the workbook leaves them out. */
  readonly one_reading_runs: number;
  readonly counts: {
    readonly agrees: number;
    readonly times_differ: number;
    readonly cloud_only: number;
    readonly workbook_only: number;
  };
  /** "same" only when every run agrees and every row is inside a run. */
  readonly verdict: 'same' | 'different';
  /** The workbook names another day. */
  readonly other_day: number | null;
}

/** The Julian day (1 to 366) of a `YYYY-MM-DD`. */
export function julianDayOf(day: string): number {
  const t = Date.parse(`${day}T00:00:00Z`);
  return Math.floor((t - Date.UTC(new Date(t).getUTCFullYear(), 0, 1)) / 86_400_000) + 1;
}

/**
 * Compare the cloud's exceedance runs of a day with the workbook's rows. Workbook times are seconds past midnight in
 * the project time zone (the tool and Field both write the project's time).
 */
export function compareSideBySide(input: {
  readonly day: string;
  readonly offsetH: number;
  readonly items: readonly CloudQcItem[];
  readonly periods: readonly WorkbookPeriod[];
  readonly toleranceS?: number;
}): SideBySideResult {
  const tol = (input.toleranceS ?? SIDE_BY_SIDE_TOLERANCE_S) * 1000;
  const midnight = localToUtcMs(Date.parse(`${input.day}T00:00:00Z`), input.offsetH);
  const at = (sod: number): number => midnight + sod * 1000;
  const runsAll = input.items.filter((i) => i.kind === 'exceedance').sort((a, b) => a.start_ms - b.start_ms);
  const runs = runsAll.filter((r) => r.end_ms > r.start_ms);
  const used = new Set<WorkbookPeriod>();
  const out: SideBySideRun[] = runs.map((r) => {
    const rows = input.periods.filter(
      (p) => at(p.start_sod) <= r.end_ms + tol && at(p.end_sod) >= r.start_ms - tol,
    );
    for (const p of rows) used.add(p);
    let verdict: RunVerdict = 'cloud_only';
    if (rows.length > 0) {
      const s = Math.min(...rows.map((p) => at(p.start_sod)));
      const e = Math.max(...rows.map((p) => at(p.end_sod)));
      const inside = rows.every(
        (p) => at(p.start_sod) >= r.start_ms - tol && at(p.end_sod) <= r.end_ms + tol,
      );
      // a run's first and last rows start and end with it, unless the run starts or ends between survey lines
      verdict =
        inside && (Math.abs(s - r.start_ms) <= tol || Math.abs(e - r.end_ms) <= tol)
          ? 'agrees'
          : 'times_differ';
    }
    return {
      start_ms: r.start_ms,
      end_ms: r.end_ms,
      readings: r.readings,
      metrics: r.metrics,
      verdict,
      rows,
    };
  });
  const workbookOnly = input.periods.filter((p) => !used.has(p));
  const count = (v: RunVerdict): number => out.filter((r) => r.verdict === v).length;
  const jday = julianDayOf(input.day);
  const other = input.periods.find((p) => p.jday !== null && p.jday !== jday)?.jday ?? null;
  const counts = {
    agrees: count('agrees'),
    times_differ: count('times_differ'),
    cloud_only: count('cloud_only'),
    workbook_only: workbookOnly.length,
  };
  return {
    day: input.day,
    jday,
    tolerance_s: tol / 1000,
    runs: out,
    workbook_only: workbookOnly,
    one_reading_runs: runsAll.length - runs.length,
    counts,
    verdict:
      other === null && counts.times_differ === 0 && counts.cloud_only === 0 && counts.workbook_only === 0
        ? 'same'
        : 'different',
    other_day: other,
  };
}

/** "13:27:13" of a workbook row's seconds past midnight. */
export function sodClock(sod: number): string {
  const s = Math.round(sod);
  return [Math.floor(s / 3600), Math.floor((s % 3600) / 60), s % 60]
    .map((x) => String(x).padStart(2, '0'))
    .join(':');
}

/** The comparison in one sentence, for the list of days compared. */
export function sideBySideSummary(r: SideBySideResult): string {
  if (r.other_day !== null)
    return `The workbook is for day ${r.other_day}, not day ${r.jday}: compare the workbook of the same day.`;
  const n = (k: number, w: string): string => `${k} ${w}${k === 1 ? '' : 's'}`;
  if (r.verdict === 'same')
    return r.runs.length === 0
      ? 'Same: no exceedances in either.'
      : `Same: all ${n(r.runs.length, 'exceedance run')} agree.`;
  const parts: string[] = [];
  if (r.counts.agrees) parts.push(`${r.counts.agrees} agree`);
  if (r.counts.times_differ) parts.push(`${r.counts.times_differ} with different times`);
  if (r.counts.cloud_only) parts.push(`${r.counts.cloud_only} only in Teslyn Cloud`);
  if (r.counts.workbook_only)
    parts.push(`${n(r.counts.workbook_only, 'workbook row')} in no Teslyn Cloud run`);
  return `Different: ${parts.join(', ')}.`;
}

/** The local clock of a cloud run's time (for the table). */
export function runClock(ms: number, offsetH: number): string {
  return localParts(ms, offsetH).hms;
}
